import { FieldErrors, FieldValues } from "react-hook-form";

/**
 * Recursively extracts all field paths that have errors from a FieldErrors object.
 */
const extractErrorPaths = (
  errors: FieldErrors<FieldValues>,
  basePath = "",
): string[] => {
  const paths: string[] = [];

  // Early return if this is a leaf error (has message property)
  // This can happen when recursing into array items that are leaf errors
  if ("message" in errors && errors.message) {
    if (basePath) {
      paths.push(basePath);
    }
    return paths;
  }

  for (const key in errors) {
    // Skip React Hook Form's special properties that aren't actual form fields
    if (key === "ref" || key === "type" || key === "types" || key === "root") {
      continue;
    }

    const error = errors[key as keyof typeof errors];
    if (!error) continue;

    const currentPath = basePath ? `${basePath}.${key}` : key;

    // Check if this is a leaf error (has a message)
    if (typeof error === "object" && "message" in error && error.message) {
      paths.push(currentPath);
    }

    // Check if this is a nested object or array
    if (typeof error === "object" && !("message" in error)) {
      if (Array.isArray(error)) {
        error.forEach((item, index) => {
          if (item) {
            paths.push(
              ...extractErrorPaths(
                item as FieldErrors<FieldValues>,
                `${currentPath}[${index}]`,
              ),
            );
          }
        });
      } else {
        paths.push(
          ...extractErrorPaths(error as FieldErrors<FieldValues>, currentPath),
        );
      }
    }
  }

  return paths;
};

type FieldPathMapping<TStepId extends string> = Array<{
  pathPrefix: string;
  stepId: TStepId;
}>;

type Step<TStepId extends string> = { id: TStepId };

export const createStepErrorNavigation = <TStepId extends string>(
  fieldPathToStepMap: FieldPathMapping<TStepId>,
) => {
  const getStepIdForFieldPath = (fieldPath: string): TStepId | undefined => {
    for (const { pathPrefix, stepId } of fieldPathToStepMap) {
      if (fieldPath.startsWith(pathPrefix)) {
        return stepId;
      }
    }
    return undefined;
  };

  const getStepsWithErrors = (errors: FieldErrors<FieldValues>): TStepId[] => {
    const errorPaths = extractErrorPaths(errors);
    const stepsWithErrors = new Set<TStepId>();

    for (const path of errorPaths) {
      const stepId = getStepIdForFieldPath(path);
      if (stepId) {
        stepsWithErrors.add(stepId);
      }
    }

    return Array.from(stepsWithErrors);
  };

  const stepHasErrors = (
    errors: FieldErrors<FieldValues>,
    stepId: TStepId,
  ): boolean => {
    return getStepsWithErrors(errors).includes(stepId);
  };

  const getFirstErrorStep = (
    errors: FieldErrors<FieldValues>,
    allSteps: Step<TStepId>[],
  ): TStepId | undefined => {
    const stepsWithErrors = getStepsWithErrors(errors);

    if (stepsWithErrors.length === 0) {
      return undefined;
    }

    for (const step of allSteps) {
      if (stepsWithErrors.includes(step.id)) {
        return step.id;
      }
    }

    return undefined;
  };

  return {
    getStepIdForFieldPath,
    getStepsWithErrors,
    stepHasErrors,
    getFirstErrorStep,
  };
};
